// @ts-nocheck
// Shared modal picker used by the sidebar's Transfer + Conf buttons.
// Fetches the list of available agents (GET API_AGENT_TRANSFER_AVAILABLE,
// cached ~10s in the parent), lets the user pick one, and fires the
// matching action:
//
//   mode = 'transfer'  → POST API_AGENT_CALL_TRANSFER/:id
//                        payload includes `type: 0 (blind) | 1 (attended)`
//   mode = 'conference' → POST API_AGENT_CONFERENCE_ADD/:id
//                        adds the agent to the current call
//
// Same behaviour ported from OnCallScreen so no telephony flow changes;
// this component just gives the (now hidden) OnCallScreen's picker a
// new home reachable from the sidebar rail's control grid.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useCallStore } from '@/router/zustand/callStore';
import { trackActivity } from '@/service/activityTracker';
import {
  API_AGENT_TRANSFER_AVAILABLE,
  API_AGENT_CALL_TRANSFER,
  API_AGENT_CONFERENCE_ADD,
} from '@/service/url';

export default function TransferConferencePicker({
  open,
  mode,
  onOpenChange,
  userData,
}) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transferType, setTransferType] = useState(0); // 0 = blind, 1 = attended
  const [busyAgentId, setBusyAgentId] = useState(null);
  const [query, setQuery] = useState('');
  const cacheRef = useRef({ agents: null, ts: 0 });

  const title = mode === 'transfer' ? 'Transfer call' : 'Add to conference';
  const subtitle =
    mode === 'transfer'
      ? 'Pick an agent to transfer the current call to.'
      : 'Pick an agent to add to the active call.';

  const activeCliNumber = useCallStore((s) => s.activeCliNumber);
  const callerNumber = useCallStore((s) => s.callerNumber);
  const sipHeaders = useCallStore((s) => s.sipHeaders);
  const pbxName = useCallStore((s) => s.pbxName);

  const getCallId = () => {
    const fromStore = sipHeaders?.['call-id'];
    if (fromStore) return String(fromStore);
    return null;
  };

  useEffect(() => {
    if (!open) return;
    // Reset per-open state.
    setError(null);
    setBusyAgentId(null);
    setQuery('');
    setTransferType(0);

    const cached = cacheRef.current;
    if (cached.agents && Date.now() - cached.ts < 10_000) {
      setAgents(cached.agents);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await getApiCall(API_AGENT_TRANSFER_AVAILABLE);
        if (cancelled) return;
        const list = Array.isArray(data?.agents) ? data.agents : [];
        cacheRef.current = { agents: list, ts: Date.now() };
        setAgents(list);
      } catch (err) {
        if (cancelled) return;
        setError(friendlyApiError(err) || 'Failed to load available agents');
        setAgents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      const name = String(a?.name || '').toLowerCase();
      const ext = String(a?.extension || a?.number || '').toLowerCase();
      return name.includes(q) || ext.includes(q);
    });
  }, [agents, query]);

  const getTransferNo = (a) => a?.extension || a?.number || null;

  const isTargetUnavailable = (err) =>
    typeof err === 'string' && err.includes('no longer available');
  const dropUnavailable = (agent) => {
    cacheRef.current = { agents: null, ts: 0 };
    setAgents((prev) => prev.filter((a) => a.id !== agent.id));
  };

  const doTransfer = async (agent) => {
    if (busyAgentId) return;
    const callId = getCallId();
    const transferNo = getTransferNo(agent);
    if (!agent?.id) { toast.error('Target Agent ID not found.'); return; }
    if (!userData?.agentData?.id) { toast.error('Initiating Agent ID not found.'); return; }
    if (!transferNo) { toast.error('No reachable number/extension for this agent'); return; }
    setBusyAgentId(agent.id);
    try {
      await postApiCall(
        API_AGENT_CALL_TRANSFER.replace(':id', encodeURIComponent(agent.id)),
        {
          requestId: `req-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
          callbackUrl: window.location.origin,
          transferNo: String(transferNo),
          type: transferType,
          activeCli: activeCliNumber || null,
          from: callerNumber || null,
          initiating_agent_id: userData?.agentData?.id,
          target_agent_id: agent.id,
          pbx_name: pbxName || null,
        },
      );
      trackActivity('transfer_complete', {
        call_id: callId, to_agent_id: agent.id, to_agent_name: agent.name || null,
        to_number: transferNo, type: transferType,
      });
      toast.success(
        `${transferType === 1 ? 'Warm' : 'Blind'} transfer initiated to ${agent.name || transferNo}`,
      );
      onOpenChange?.(false);
    } catch (err) {
      if (isTargetUnavailable(err)) dropUnavailable(agent);
      toast.error(friendlyApiError(err) || 'Failed to transfer call');
    } finally {
      setBusyAgentId(null);
    }
  };

  const doConference = async (agent) => {
    if (busyAgentId) return;
    const callId = getCallId();
    const transferNo = getTransferNo(agent);
    if (!agent?.id) { toast.error('Target Agent ID not found.'); return; }
    if (!userData?.agentData?.id) { toast.error('Initiating Agent ID not found.'); return; }
    if (!transferNo) { toast.error('No reachable number/extension for this agent'); return; }
    setBusyAgentId(agent.id);
    try {
      await postApiCall(
        API_AGENT_CONFERENCE_ADD.replace(':id', encodeURIComponent(agent.id)),
        {
          requestId: `req-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
          callbackUrl: window.location.origin,
          transferNo: String(transferNo),
          activeCli: activeCliNumber || null,
          from: callerNumber || null,
          initiating_agent_id: userData?.agentData?.id,
          target_agent_id: agent.id,
          pbx_name: pbxName || null,
        },
      );
      trackActivity('conference_add', {
        call_id: callId, to_agent_id: agent.id, to_agent_name: agent.name || null,
        to_number: transferNo,
      });
      toast.success(`Agent added to conference: ${agent.name || transferNo}`);
      onOpenChange?.(false);
    } catch (err) {
      if (isTargetUnavailable(err)) dropUnavailable(agent);
      toast.error(friendlyApiError(err) || 'Failed to add agent to conference');
    } finally {
      setBusyAgentId(null);
    }
  };

  const onPick = (agent) => (mode === 'transfer' ? doTransfer : doConference)(agent);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1100] max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </DialogHeader>

        {mode === 'transfer' && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={transferType === 0 ? 'default' : 'outline'}
              onClick={() => setTransferType(0)}
              disabled={Boolean(busyAgentId)}
              className="flex-1"
            >
              Blind
            </Button>
            <Button
              size="sm"
              variant={transferType === 1 ? 'default' : 'outline'}
              onClick={() => setTransferType(1)}
              disabled={Boolean(busyAgentId)}
              className="flex-1"
            >
              Warm
            </Button>
          </div>
        )}

        <Input
          type="text"
          placeholder="Search agents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading || Boolean(busyAgentId)}
        />

        <div className="max-h-72 overflow-y-auto rounded-md border border-border/60 bg-background">
          {loading ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Loading agents…</div>
          ) : error ? (
            <div className="p-6 text-center text-xs text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              {agents.length === 0 ? 'No agents available right now.' : 'No matches.'}
            </div>
          ) : (
            filtered.map((a) => {
              const busy = busyAgentId === a.id;
              const num = getTransferNo(a);
              return (
                <button
                  key={a.id}
                  type="button"
                  className="flex w-full items-center gap-3 border-b border-border/40 px-3 py-2 text-left transition-colors hover:bg-accent last:border-b-0 disabled:opacity-60"
                  onClick={() => onPick(a)}
                  disabled={Boolean(busyAgentId)}
                >
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                    {String(a.name || 'A').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {a.name || `Agent ${a.id}`}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {num ? `x${num}` : 'No extension'}
                      {/* Was `a.source_label` — never actually set by
                        * either side, so this line silently rendered
                        * nothing extra. Backend already resolves the
                        * agent's active device and call-group membership
                        * for this exact endpoint; just wasn't being read. */}
                      {a.active_profile_type ? ` · ${a.active_profile_type}` : ''}
                    </div>
                    {(a.is_team_lead || a.call_groups?.length > 0) && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {a.is_team_lead && (
                          <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary">
                            Team Lead
                          </span>
                        )}
                        {a.call_groups?.length > 0 && (
                          <span className="truncate text-[10px] text-muted-foreground/70">
                            {a.call_groups.map((g) => g.name).join(', ')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] font-semibold text-primary">
                    {busy ? '…' : mode === 'transfer' ? 'Send' : 'Add'}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={Boolean(busyAgentId)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
